import { PlanTrailActionContext, getMap, getLayerRegistry, safeRemoveLayer } from "./types";
import { MarkerFeature } from "../../../../../utils/models/MarkerFeature";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import { plainToClass } from "class-transformer";
import { MapLayerCategory } from "../../../../../utils/layer-registry";
import mapboxgl from "mapbox-gl";
import type { FeatureCollection, Feature, Point } from 'geojson';
import { ServiceProvider } from "../../../services/ServiceProvider";
import {sanitizeCoords} from "../../../../../utils/sanitizeCoords";
import { RemoveMarkerCommand } from "../../undo/commands/RemoveMarkerCommand";
import { MoveMarkerCommand } from "../../undo/commands/MoveMarkerCommand";
import { RemoveAllControlPointsCommand } from "../../undo/commands/RemoveAllControlPointsCommand";
import { detectRouteJoins, detectStageEndMarkers, findSnapConnectTarget, isWithinSnapRadius, orderedDayStages, SnapConnectTarget } from "../../../services/stage-connectors";
import { resolveAttachedDestinations, stageEndDayForMarker, stageStartDayForMarker, StageDestination } from "../../../services/stage-destinations";
import { resolveAttachTarget } from "./attach-target";
import { mergeLineStrings } from "../../../mergeLineStrings";
import { announceRouteChanged } from "../../../utils/routeChangedNotice";

// Store function references for cleanup
let functionRefs: { [key: string]: Function[] } = {};

// Consolidated marker layer/source identifiers and in-memory data store
const CONSOLIDATED_MARKER_SOURCE = 'consolidated-routes-markers';
const CONSOLIDATED_MARKER_LAYER = 'consolidated-routes-marker-layer';
let consolidatedMarkersData: FeatureCollection | null = null;
let markerIndexById: Record<string, number> = {};

// Snap-to-connect: highlight ring shown on the next stage's start point
// while a dragged end marker is close enough to snap onto it. The radius and
// the hit test itself live in services/stage-connectors.ts, shared with the
// floating-badge drag in actions/stage-connectors.ts.
const SNAP_TARGET_SOURCE = 'snap-connect-target';
const SNAP_TARGET_LAYER = 'snap-connect-target-layer';

export function showSnapTarget(map: mapboxgl.Map, position: [number, number]) {
  const data: any = {
    type: 'FeatureCollection',
    features: [{ type: 'Feature', geometry: { type: 'Point', coordinates: position }, properties: {} }]
  };
  const source = map.getSource(SNAP_TARGET_SOURCE) as mapboxgl.GeoJSONSource | undefined;
  if (source) {
    try { source.setData(data); } catch (_) {}
  } else {
    map.addSource(SNAP_TARGET_SOURCE, { type: 'geojson', data });
  }
  if (!map.getLayer(SNAP_TARGET_LAYER)) {
    map.addLayer({
      id: SNAP_TARGET_LAYER,
      type: 'circle',
      source: SNAP_TARGET_SOURCE,
      paint: {
        'circle-radius': 16,
        'circle-color': 'rgba(253, 223, 0, 0.25)',
        'circle-stroke-color': '#fddf00',
        'circle-stroke-width': 3,
        'circle-pitch-alignment': 'map'
      }
    });
  }
}

export function hideSnapTarget(map: mapboxgl.Map) {
  try {
    if (map.getLayer(SNAP_TARGET_LAYER)) map.removeLayer(SNAP_TARGET_LAYER);
    if (map.getSource(SNAP_TARGET_SOURCE)) map.removeSource(SNAP_TARGET_SOURCE);
  } catch (_) {}
}

// Clean up all marker event listeners (called before re-rendering routes)
export function cleanupAllMarkerListeners() {
  const map = getMap();
  if (!map) return;

  console.log('[MARKERS] cleanupAllMarkerListeners called, cleaning up', Object.keys(functionRefs).length, 'layers');
  // Remove all stored event listeners
  Object.keys(functionRefs).forEach((layerId) => {
    if (functionRefs[layerId]) {
      functionRefs[layerId].forEach((ref) => {
        map.off("mouseenter", layerId, ref);
        map.off("mouseleave", layerId, ref);
        map.off("mousedown", layerId, ref);
        map.off("touchstart", layerId, ref);
      });
    }
  });

  // Clear the references
  functionRefs = {};
  console.log('[MARKERS] cleanupAllMarkerListeners complete');
}

export const markerActions = {
  /**
   * Build a consolidated FeatureCollection of draggable control points.
   * With an active route: that route's markers only (focused editing).
   * With NOTHING active: EVERY route's markers — mains, alts, spurs and
   * transport alike — render and are editable; grabbing one auto-activates
   * its route mid-gesture (whole-trail editing).
   */
  buildConsolidatedMarkersData({ state }: PlanTrailActionContext): FeatureCollection | null {
    const activeRoute = state.activeRouteKey ? state.routes[state.activeRouteKey] : null;

    const routesToRender: Array<{ key: string; route: any }> = activeRoute
      ? [{ key: state.activeRouteKey!, route: activeRoute }]
      : Object.keys(state.routes)
          .map(key => ({ key, route: state.routes[key] }))
          .filter(entry => entry.route.markers.length > 0)
          .sort((a, b) => a.route.position - b.route.position);

    if (routesToRender.length === 0 ||
        routesToRender.every(entry => entry.route.markers.length === 0)) {
      return null;
    }

    // Endpoint markers covered by a stage marker are suppressed — the
    // connector handle (shared point) or the floating numbered badge is
    // their drag affordance instead. Without this, the badge sits exactly
    // on top of a hidden endpoint icon and grabs go to the wrong thing.
    const suppressed = new Set<string>();
    for (const stageEnd of detectStageEndMarkers(state.routes, undefined, state.disconnectedStageIds)) {
      if (stageEnd.connector) {
        suppressed.add(`${stageEnd.connector.prevRouteKey}:${stageEnd.connector.prevMarkerIndex}`);
        suppressed.add(`${stageEnd.connector.nextRouteKey}:${stageEnd.connector.nextMarkerIndex}`);
      } else {
        const stageRoute = state.routes[stageEnd.routeKey];
        if (stageRoute && stageRoute.markers.length > 0) {
          suppressed.add(`${stageEnd.routeKey}:${stageRoute.markers.length - 1}`);
        }
      }
    }

    // Same for joins between routes of the same day: the small join handle
    // is the drag affordance for both endpoints.
    for (const join of detectRouteJoins(state.routes, undefined, state.disconnectedStageIds)) {
      suppressed.add(`${join.prevRouteKey}:${join.prevMarkerIndex}`);
      suppressed.add(`${join.nextRouteKey}:${join.nextMarkerIndex}`);
    }

    // End markers attached to a destination are suppressed for the same
    // reason — the destination ring (stage-connector handle layer) is their
    // drag affordance. This is what hides the trail-finish control point
    // when the final day is attached.
    for (const entry of Object.values(resolveAttachedDestinations(state.routes, state.stageDestinations))) {
      suppressed.add(`${entry.routeKey}:${entry.markerIndex}`);
    }

    const features: Feature<Point>[] = [] as any;
    markerIndexById = {};

    for (const { key, route } of routesToRender) {
      const isOffshoot = (route.route_style ?? 'route') !== 'route';
      const isSelectedRoute = key === state.activeRouteKey;
      for (let i = 0; i < route.markers.length; i++) {
        if (suppressed.has(`${key}:${i}`)) continue;
        const m = route.markers[i];
        const coords = m.markerGeometry.coordinates as [number, number];
        const isFirst = i === 0;
        const isLast = i === route.markers.length - 1;
        // Offshoots (alt/spur/transport) only show their start/end markers
        // when selected — in whole-trail mode their endpoints are noise
        if (isOffshoot && !isSelectedRoute && (isFirst || isLast)) continue;
        const markerId = `route-${route.id}-marker-${i}`;
        // Index into the features array (NOT the marker index) — suppression
        // means the two can diverge.
        markerIndexById[markerId] = features.length;

        const feature: Feature<Point> = {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: [coords[0], coords[1]]
          },
          properties: {
            routeId: route.id,
            routeKey: key,
            markerIndex: i,
            markerId,
            position: isFirst ? 'start' : (isLast ? 'end' : 'mid'),
            isActive: true
          }
        } as any;

        features.push(feature);
      }
    }

    return {
      type: 'FeatureCollection',
      features
    };
  },

  /**
   * Add a single consolidated layer/source for markers with data-driven styling
   */
  async addConsolidatedMarkersToMap({ state, commit, dispatch }: PlanTrailActionContext) {
    const map = getMap();
    const layerRegistry = getLayerRegistry();

    // Build dataset from active route only (disabled routes have no markers)
    consolidatedMarkersData = (markerActions as any).buildConsolidatedMarkersData({ state } as any);
    console.log('[MARKERS] addConsolidatedMarkersToMap called, has data:', !!consolidatedMarkersData);
    if (!consolidatedMarkersData) {
      console.log('[MARKERS] No markers data, cleaning up listeners and removing layer');
      // Clean up event listeners before removing layer
      cleanupAllMarkerListeners();

      // If nothing to show, remove existing layer/source once
      safeRemoveLayer(CONSOLIDATED_MARKER_LAYER);
      if (map.getSource(CONSOLIDATED_MARKER_SOURCE)) {
        try { map.removeSource(CONSOLIDATED_MARKER_SOURCE); } catch (_) {}
      }
      return;
    }

    // If already present, just update data to avoid flashing
    const existingSource = map.getSource(CONSOLIDATED_MARKER_SOURCE) as mapboxgl.GeoJSONSource | undefined;
    const existingLayer = map.getLayer(CONSOLIDATED_MARKER_LAYER);
    console.log('[MARKERS] Existing source:', !!existingSource, 'Existing layer:', !!existingLayer);
    console.log('[MARKERS] Has listeners:', !!functionRefs[CONSOLIDATED_MARKER_LAYER], 'Count:', functionRefs[CONSOLIDATED_MARKER_LAYER]?.length || 0);
    if (existingSource && existingLayer) {
      try { existingSource.setData(consolidatedMarkersData as any); } catch (_) {}
      // Check if listeners are already attached
      if (functionRefs[CONSOLIDATED_MARKER_LAYER] && functionRefs[CONSOLIDATED_MARKER_LAYER].length > 0) {
        console.log('[MARKERS] Listeners already attached, returning');
        return;
      }
      console.log('[MARKERS] Listeners missing, will re-attach');
      // If listeners are missing, fall through to re-attach them below (but don't re-add source/layer)
    } else {
      // Source or layer doesn't exist, so create them
      // Add source
      layerRegistry.addSource(CONSOLIDATED_MARKER_SOURCE, {
        type: 'geojson',
        data: consolidatedMarkersData
      }, true);

      // Data-driven icon selection based on position
      const iconImageExpr: any = [
        'case',
        ['==', ['get', 'position'], 'start'], 'hike-control-point-start',
        ['==', ['get', 'position'], 'end'], 'hike-control-point-end',
        'hike-control-point-mid'
      ];

      const layer: mapboxgl.SymbolLayer = {
        id: CONSOLIDATED_MARKER_LAYER,
        type: 'symbol',
        source: CONSOLIDATED_MARKER_SOURCE,
        layout: {
          'icon-image': iconImageExpr,
          'icon-size': 0.25,
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'icon-anchor': 'center'
        },
        paint: {
          'icon-opacity': 1.0
        }
      };

      layerRegistry.addLayer(layer, MapLayerCategory.Annotations, { z: 13 });
    }

    // Ensure layer is actually added before attaching listeners
    if (!map.getLayer(CONSOLIDATED_MARKER_LAYER)) {
      console.error('[MARKERS] Layer was not added to map, cannot attach listeners');
      return;
    }

    // Clean up any existing listeners before attaching new ones
    console.log('[MARKERS] Cleaning up existing listeners before attaching new ones');
    if (functionRefs[CONSOLIDATED_MARKER_LAYER]) {
      functionRefs[CONSOLIDATED_MARKER_LAYER].forEach((ref) => {
        map.off("mouseenter", CONSOLIDATED_MARKER_LAYER, ref);
        map.off("mouseleave", CONSOLIDATED_MARKER_LAYER, ref);
        map.off("mousedown", CONSOLIDATED_MARKER_LAYER, ref);
        map.off("touchstart", CONSOLIDATED_MARKER_LAYER, ref);
      });
    }

    // Attach consolidated drag listeners
    console.log('[MARKERS] Attaching new listeners');
    const canvas = map.getCanvasContainer();

    let startClickTime: number | null = null;
    let lastDragLngLat: mapboxgl.LngLatLike | null = null;
    let currentMarkerId: string | null = null;
    let currentMarkerIndex: number | null = null;
    let currentRouteKey: string | null = null;
    let snapConnectTarget: SnapConnectTarget | null = null;
    let snapConnectEngaged = false;
    // Attach-to-destination: dragging a stage END near a POI/waypoint/
    // highlight shows the plus cursor + ring; dropping attaches it.
    let attachDayNumber: number | null = null;
    // Same, but for a day's stage START (marker index 0 of that day's first
    // main-line route) — a separate flag since it attaches via
    // attachTrailStart (a single slot) rather than attachStageDestination.
    let attachStartDayNumber: number | null = null;
    let attachTarget: StageDestination | null = null;
    let attachEngaged = false;
    // Currently-engaged item, kept across moves so the snap has hysteresis
    let engagedAttachTarget: StageDestination | null = null;

    async function onMove(e: mapboxgl.MapMouseEvent) {
      console.log('[MARKERS] onMove called, currentMarkerIndex:', currentMarkerIndex);
      const src = map.getSource(CONSOLIDATED_MARKER_SOURCE) as mapboxgl.GeoJSONSource;
      if (!src || !consolidatedMarkersData || currentMarkerIndex == null) {
        console.log('[MARKERS] onMove early return - src:', !!src, 'data:', !!consolidatedMarkersData, 'index:', currentMarkerIndex);
        return;
      }
      let coords = e.lngLat;

      // Snap-to-connect: dragging a stage-end near the next stage's start
      // magnetically snaps onto it and shows a highlight ring — dropping
      // there joins the two routes as a connected stage.
      snapConnectEngaged = false;
      attachEngaged = false;
      attachTarget = null;
      if (snapConnectTarget) {
        if (isWithinSnapRadius(map.project(snapConnectTarget.position as any), e.point)) {
          snapConnectEngaged = true;
          coords = new mapboxgl.LngLat(snapConnectTarget.position[0], snapConnectTarget.position[1]);
          showSnapTarget(map, snapConnectTarget.position);
        }
      }

      // Attach-to-destination: a dragged stage END (or the trail START) in
      // the VICINITY of a POI/waypoint/highlight snaps onto the item (with
      // hysteresis so it doesn't flicker) — dropping there attaches it.
      if (!snapConnectEngaged && (attachDayNumber != null || attachStartDayNumber != null)) {
        const target = resolveAttachTarget(map, e.point, engagedAttachTarget);
        if (target) {
          attachEngaged = true;
          attachTarget = target;
          engagedAttachTarget = target;
          coords = new mapboxgl.LngLat(target.coordinates[0], target.coordinates[1]);
          showSnapTarget(map, target.coordinates);
        } else {
          engagedAttachTarget = null;
        }
      }

      if (!snapConnectEngaged && !attachEngaged) {
        hideSnapTarget(map);
      }

      lastDragLngLat = coords;
      console.log('[MARKERS] onMove dragging to:', coords);

      // Update in-memory feature collection
      const featureIdx = markerIndexById[currentMarkerId!];
      if (featureIdx != null && consolidatedMarkersData.features[featureIdx]) {
        (consolidatedMarkersData.features[featureIdx] as any).geometry.coordinates = [coords.lng, coords.lat];
        try { src.setData(consolidatedMarkersData as any); } catch (_) {}
      }

      // Update drag preview if snapping — scoped to the dragged marker's own
      // route (which may not be active in whole-trail editing). While snapped
      // to an attach target (or connect target) the marker is pinned to the
      // item, so suppress the competing gray "nearest route" preview — that
      // overlap is what made attaching feel glitchy.
      const stateAny = state as any;
      console.log('[MARKERS] onMove - isSnap:', stateAny.isSnap);
      if (stateAny.isSnap && currentRouteKey != null) {
        if (snapConnectEngaged || attachEngaged) {
          await (dispatch as any)('hideDragPreviewLine');
        } else {
          await (dispatch as any)('updateDragPreview', [coords.lng, coords.lat]);
        }
      }
      canvas.style.cursor = (snapConnectEngaged || attachEngaged) ? 'copy' : 'grabbing';
    }

    async function onUp(e: mapboxgl.MapMouseEvent) {
      map.off('mousemove', onMove);
      map.off('touchmove', onMove as any);
      document.removeEventListener('keydown', onKeyDown);

      const endClickTime = Date.now();
      const stateAny = state as any;

      hideSnapTarget(map);

      // Attach-to-destination drop: the attach action moves the stage end
      // (or trail start) exactly onto the item (bringing a connected
      // next-day start along, for a stage end), records the destination and
      // refreshes all decorations.
      if (attachEngaged && attachTarget && (attachDayNumber != null || attachStartDayNumber != null)) {
        const destination = attachTarget;
        const attachRouteKey = currentRouteKey;
        const attachMarkerIndex = currentMarkerIndex;
        startClickTime = null;
        lastDragLngLat = null;
        currentMarkerId = null;
        currentMarkerIndex = null;
        currentRouteKey = null;
        snapConnectTarget = null;
        snapConnectEngaged = false;
        attachDayNumber = null;
        attachStartDayNumber = null;
        attachTarget = null;
        attachEngaged = false;
        engagedAttachTarget = null;

        // The network-snapped preview would drift off the item — use the
        // exact-position attach path instead.
        (dispatch as any)('cancelDragPreview');

        // Same as the item's own "Add to route" — see dropEndpointOnDestination.
        if (attachRouteKey != null && attachMarkerIndex != null) {
          await (dispatch as any)('dropEndpointOnDestination', {
            routeKey: attachRouteKey, markerIndex: attachMarkerIndex, destination,
          });
        }
        canvas.style.cursor = 'move';
        return;
      }

      // Snap-to-connect drop: land EXACTLY on the next stage's start and
      // re-enable the connector if this pair had been manually disconnected.
      if (snapConnectEngaged && snapConnectTarget) {
        lastDragLngLat = new mapboxgl.LngLat(snapConnectTarget.position[0], snapConnectTarget.position[1]);
        stateAny.disconnectedStageIds = (stateAny.disconnectedStageIds || [])
          .filter((id: string) => id !== snapConnectTarget!.connectorId);
        // The network-snapped preview position would drift off the target —
        // use the plain marker-move path so the endpoints actually meet.
        (dispatch as any)('cancelDragPreview');
      }

      // Try to finalize preview if snap is enabled and preview was started
      let previewApplied = false;
      if (stateAny.isSnap && stateAny.dragPreview.isDragging) {
        previewApplied = await (dispatch as any)('finalizeDragPreview');
      }

      // If preview wasn't applied (either not using snap or preview wasn't ready),
      // fall back to normal marker movement
      if (!previewApplied && currentMarkerIndex != null && lastDragLngLat) {
        const targetRoute = currentRouteKey
          ? stateAny.routes[currentRouteKey]
          : (stateAny.activeRouteKey ? stateAny.routes[stateAny.activeRouteKey] : null);
        if (targetRoute) {
          const marker = targetRoute.markers[currentMarkerIndex];
          if (marker) {
            // Capture old position before updating for the undo command
            const oldPosition = [...marker.markerGeometry.coordinates];

            marker.markerGeometry.coordinates = [lastDragLngLat.lng as any, lastDragLngLat.lat as any];
            await (dispatch as any)('markerMoved', { marker, oldPosition, routeKey: currentRouteKey });
          }
        }
      }
      if (previewApplied || (currentMarkerIndex != null && lastDragLngLat)) announceRouteChanged();

      canvas.style.cursor = 'move';

      // Reset drag state
      startClickTime = null;
      lastDragLngLat = null;
      currentMarkerId = null;
      currentMarkerIndex = null;
      currentRouteKey = null;
      snapConnectTarget = null;
      snapConnectEngaged = false;
      attachDayNumber = null;
      attachStartDayNumber = null;
      attachTarget = null;
      attachEngaged = false;
      engagedAttachTarget = null;
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        map.off('mousemove', onMove);
        map.off('touchmove', onMove as any);
        document.removeEventListener('keydown', onKeyDown);

        hideSnapTarget(map);
        attachDayNumber = null;
        attachStartDayNumber = null;
        attachTarget = null;
        attachEngaged = false;
        engagedAttachTarget = null;

        // Revert visual position to store state
        consolidatedMarkersData = (markerActions as any).buildConsolidatedMarkersData({ state } as any);
        const src = map.getSource(CONSOLIDATED_MARKER_SOURCE) as mapboxgl.GeoJSONSource;
        if (src && consolidatedMarkersData) {
          try { src.setData(consolidatedMarkersData as any); } catch (_) {}
        }

        // Cancel preview if active
        const stateAny = state as any;
        if (stateAny.dragPreview.isDragging) {
          (dispatch as any)('cancelDragPreview');
        }
        canvas.style.cursor = 'move';
      }
    }

    function enter(e: any) {
      console.log('[MARKERS] Mouse entered marker layer');
      canvas.style.cursor = 'move';
    }

    function leave(e: any) {
      console.log('[MARKERS] Mouse left marker layer');
      canvas.style.cursor = 'crosshair';
    }

    function mouseDown(e: mapboxgl.MapMouseEvent & { features?: any[] }) {
      console.log('[MARKERS] mouseDown triggered', e.features);
      e.preventDefault();

      const feat = e.features && e.features[0];
      if (!feat) {
        return;
      }

      // The marker's own route — in whole-trail mode (nothing active) this
      // may differ from the active route
      const markerRouteKey = feat.properties?.routeKey || null;
      const markerRoute = markerRouteKey ? (state as any).routes[markerRouteKey] : null;

      // Synchronously check if editing is allowed (no modal). Gate on the
      // marker's OWN route so whole-trail mode still warns for references.
      if (typeof window.canEditReference === 'function' && !window.canEditReference(markerRoute)) {
        console.log('[MARKERS] Cannot edit - user needs to confirm referenced route edit first');
        // Show the warning modal
        if (typeof window.checkReferenceEditWarning === 'function') {
          window.checkReferenceEditWarning(markerRoute).then(confirmed => {
            if (confirmed) {
              console.log('[MARKERS] User confirmed - they can now drag markers');
            }
          });
        }
        return;
      }

      startClickTime = Date.now();
      currentMarkerId = feat.properties?.markerId || null;
      currentMarkerIndex = feat.properties?.markerIndex ?? null;
      console.log('[MARKERS] Starting drag for marker:', currentMarkerId, 'index:', currentMarkerIndex);

      const stateAny = state as any;

      // Editing does NOT activate a route — the drag is scoped to the
      // marker's own route (activation only happens via the panel cards or
      // the numbered stage markers).
      currentRouteKey = markerRouteKey;

      // Snap-to-connect candidate: end marker of a stage's last main route
      // can snap FORWARD onto the next stage's start, and the start marker
      // of a stage's first main route can snap BACKWARD onto the previous
      // stage's end (mains only) — see findSnapConnectTarget.
      snapConnectTarget = (currentRouteKey != null && currentMarkerIndex != null)
        ? findSnapConnectTarget(stateAny.routes, currentRouteKey, currentMarkerIndex)
        : null;
      snapConnectEngaged = false;

      // Attach-to-destination candidate: only a day's END marker (including
      // the trail finish) can attach to a POI/waypoint/highlight
      attachDayNumber = (currentRouteKey != null && currentMarkerIndex != null)
        ? stageEndDayForMarker(stateAny.routes, currentRouteKey, currentMarkerIndex)
        : null;
      // Same, but for a day's stage START (marker index 0 of that day's
      // first main-line route) — mutually exclusive with attachDayNumber.
      attachStartDayNumber = (currentRouteKey != null && currentMarkerIndex != null)
        ? stageStartDayForMarker(stateAny.routes, currentRouteKey, currentMarkerIndex)
        : null;
      attachTarget = null;
      attachEngaged = false;
      engagedAttachTarget = null;

      if (stateAny.isSnap && currentMarkerIndex != null && currentRouteKey) {
        (dispatch as any)('initializeDragPreview', {
          markerIndex: currentMarkerIndex,
          routeId: currentRouteKey,  // Pass the route key, not the internal ID
          position: [feat.geometry.coordinates[0], feat.geometry.coordinates[1]]
        });
      }

      map.on('mousemove', onMove);
      map.once('mouseup', onUp);
      document.addEventListener('keydown', onKeyDown);
    }

    function touchStart(e: any) {
      if (!e.points || e.points.length !== 1) return;
      e.preventDefault();
      map.on('touchmove', onMove as any);
      map.once('touchend', onUp as any);
    }

    // Store refs for cleanup
    functionRefs[CONSOLIDATED_MARKER_LAYER] = [onMove, onUp, enter, leave, mouseDown, touchStart];
    map.on('mouseenter', CONSOLIDATED_MARKER_LAYER, enter);
    map.on('mouseleave', CONSOLIDATED_MARKER_LAYER, leave);
    map.on('mousedown', CONSOLIDATED_MARKER_LAYER, mouseDown as any);
    map.on('touchstart', CONSOLIDATED_MARKER_LAYER, touchStart as any);
    console.log('[MARKERS] Listeners attached successfully');
  },

  /**
   * Remove consolidated marker layer and source
   */
  async removeConsolidatedMarkersFromMap({ }: PlanTrailActionContext) {
    const map = getMap();
    if (!map) return;
    safeRemoveLayer(CONSOLIDATED_MARKER_LAYER);
    if (map.getSource(CONSOLIDATED_MARKER_SOURCE)) {
      try { map.removeSource(CONSOLIDATED_MARKER_SOURCE); } catch (_) {}
    }
  },
  // Legacy function - removed. Use addAllRoutesToMap instead.

  // Legacy function - removed. Use addRouteMarkerToMap instead.

  async removeMarkerById({ state, commit, dispatch }: PlanTrailActionContext, { routeKey, markerIndex }: { routeKey: string, markerIndex: number }) {
    const route = state.routes[routeKey];
    if (!route || markerIndex < 0 || markerIndex >= route.markers.length) {
      console.error('Invalid route or marker index');
      return;
    }

    // Capture state before removal for the undo command
    const removedMarker = route.markers[markerIndex];
    let lsStartIndex: number;
    let lineStringsBefore: LineStringFeature[];
    let lineStringsBeforeCount: number;

    // Determine which linestrings are affected
    if (route.markers.length <= 2) {
      // Will clear all linestrings
      lsStartIndex = 0;
      lineStringsBefore = [...route.lineStrings];
      lineStringsBeforeCount = route.lineStrings.length;
    } else if (markerIndex === 0) {
      lsStartIndex = 0;
      lineStringsBefore = route.lineStrings.length > 0 ? [route.lineStrings[0]] : [];
      lineStringsBeforeCount = lineStringsBefore.length;
    } else if (markerIndex >= route.markers.length - 1) {
      lsStartIndex = route.lineStrings.length > 0 ? route.lineStrings.length - 1 : 0;
      lineStringsBefore = route.lineStrings.length > 0 ? [route.lineStrings[route.lineStrings.length - 1]] : [];
      lineStringsBeforeCount = lineStringsBefore.length;
    } else {
      // Middle marker — two adjacent linestrings
      lsStartIndex = markerIndex - 1;
      lineStringsBefore = route.lineStrings.slice(markerIndex - 1, markerIndex + 1);
      lineStringsBeforeCount = 2;
    }

    // Now perform the removal
    route.markers.splice(markerIndex, 1);

    // Handle linestrings based on the marker position
    if (route.markers.length === 0) {
      route.lineStrings = [];
    } else if (route.markers.length === 1) {
      route.lineStrings = [];
    } else if (markerIndex === 0) {
      if (route.lineStrings.length > 0) {
        route.lineStrings.splice(0, 1);
      }
    } else if (markerIndex >= route.markers.length) {
      if (route.lineStrings.length > 0) {
        route.lineStrings.splice(route.lineStrings.length - 1, 1);
      }
    } else {
      if (markerIndex - 1 < route.lineStrings.length && markerIndex < route.lineStrings.length) {
        route.lineStrings.splice(markerIndex - 1, 2, mergeLineStrings(route, markerIndex - 1, markerIndex));
      }
    }

    // Capture linestrings after removal — determine how many are at the affected position now
    let lineStringsAfter: LineStringFeature[];
    if (route.markers.length <= 1) {
      // 0 or 1 markers left — no linestrings
      lineStringsAfter = [];
    } else if (markerIndex === 0 || markerIndex >= route.markers.length) {
      // Removed first or last — one linestring was removed, nothing replaced it
      lineStringsAfter = [];
    } else {
      // Middle marker — two linestrings were replaced by one merged linestring
      lineStringsAfter = lsStartIndex < route.lineStrings.length
        ? [route.lineStrings[lsStartIndex]]
        : [];
    }

    // Push command
    state.undoManager.push(new RemoveMarkerCommand({
      routeKey,
      markerIndex,
      removedMarker,
      lineStringsBefore,
      lineStringsAfter,
      lineStringsStartIndex: lsStartIndex,
      lineStringsBeforeCount,
    }));

    route.updated_at = new Date();
    await dispatch('updateRouteAndMarkers');
  },

  async shouldRemoveMarker({ state, commit, dispatch }: PlanTrailActionContext, marker: MarkerFeature) {
    // This function is deprecated - use removeMarkerById instead
    console.warn('shouldRemoveMarker is deprecated, use removeMarkerById');
    return;
  },

  async removeAllControlPoints({ state, dispatch }: PlanTrailActionContext) {
    const routeKey = state.activeRouteKey;
    if (!routeKey) return;

    const route = state.routes[routeKey];
    if (!route || route.markers.length <= 2 || route.lineStrings.length === 0) return;

    const markersBefore = JSON.parse(JSON.stringify(route.markers));
    const lineStringsBefore = JSON.parse(JSON.stringify(route.lineStrings));

    const firstMarker = route.markers[0];
    const lastMarker = route.markers[route.markers.length - 1];

    const mergedLineString = mergeLineStrings(route, 0, route.lineStrings.length - 1);

    route.markers = [firstMarker, lastMarker];
    route.lineStrings = [mergedLineString];
    route.updated_at = new Date();

    state.undoManager.push(new RemoveAllControlPointsCommand({
      routeKey,
      markersBefore,
      lineStringsBefore,
      markersAfter: JSON.parse(JSON.stringify(route.markers)),
      lineStringsAfter: JSON.parse(JSON.stringify(route.lineStrings)),
    }));

    await dispatch('updateRouteAndMarkers');
  },

  // Legacy function - removed. Use removeRouteMarkerFromMap instead.

  // Legacy function - removed. Use createRouteMarkerLayer instead.

  // Legacy function - removed. Use addAllRoutesToMap instead.

  addListenersForMarker({ state, dispatch, getters }: PlanTrailActionContext, feature: MarkerFeature | { marker: MarkerFeature, layerId: string }) {
    const map = getMap();
    const canvas = map.getCanvasContainer();

    // Handle both old and new call signatures
    let markerFeature: MarkerFeature;
    let id: string;

    if ('marker' in feature && 'layerId' in feature) {
      // New signature with explicit layer ID
      markerFeature = feature.marker;
      id = feature.layerId;
    } else {
      // Old signature - feature is the marker
      markerFeature = feature as MarkerFeature;
      id = markerFeature.markerProperties.id;
    }

    // Get services
    const services = ServiceProvider.getInstance().getServices();
    const { mapService, eventBus } = services;

    const added = Date.now();

    async function onMove(e: mapboxgl.MapMouseEvent) {
      // Check if the source still exists before trying to update it
      const geoJsonSource = map.getSource(id) as mapboxgl.GeoJSONSource;
      if (!geoJsonSource) {
        // Source has been removed, clean up listeners
        map.off("mousemove", onMove);
        map.off("touchmove", onMove);
        return;
      }

      const coords = e.lngLat;
      canvas.style.cursor = "grabbing";
      markerFeature.markerGeometry.coordinates = [coords.lng, coords.lat];
      // @ts-ignore
      geoJsonSource.setData(markerFeature.toGeoJson());
      
      // Update drag preview
      if (state.isSnap) {
        await dispatch('updateDragPreview', [coords.lng, coords.lat]);
      }
    }

    async function onUp(e: mapboxgl.MapMouseEvent) {
      map.off("mousemove", onMove);
      map.off("touchmove", onMove);
      document.removeEventListener('keydown', onKeyDown);
      endClickTime = Date.now();
      if (startClickTime != null && endClickTime - startClickTime < 200) {
        startClickTime = null;
        await dispatch('shouldRemoveMarker', markerFeature);
      } else {
        // Try to apply preview if snap is enabled
        let previewApplied = false;
        if (state.isSnap && state.dragPreview.isDragging) {
          previewApplied = await dispatch('finalizeDragPreview');
        }

        // If preview wasn't applied, fall back to regular marker move
        if (!previewApplied) {
          await dispatch('markerMoved', markerFeature);
        }
        announceRouteChanged();
      }
      canvas.style.cursor = "move";
    }

    function enterStyleMarker() {
      if (!map.getLayer(id)) return;
      map.setPaintProperty(id, "icon-halo-color", "#3887be");
      map.setPaintProperty(id, "icon-halo-width", 3);
      canvas.style.cursor = "move";
    }

    function leaveStyleMarker() {
      if (map.getLayer(id) != null) {
        map.setPaintProperty(id, "icon-halo-color", "#ffffff");
        map.setPaintProperty(id, "icon-halo-width", 2);
      }
      canvas.style.cursor = "crosshair";
    }

    function mouseDown(e: mapboxgl.MapMouseEvent) {
      // Prevent the default map drag behavior.
      startClickTime = Date.now();
      e.preventDefault();
      canvas.style.cursor = "grab";
      
      // Initialize drag preview if snap is enabled
      if (state.isSnap) {
        const routeId = getters.activeRouteId;
        const markerIndex = getters.activeRoute.markers.findIndex(
          (m: MarkerFeature) => m.markerProperties.id === markerFeature.markerProperties.id
        );
        
        if (routeId && markerIndex >= 0) {
          dispatch('initializeDragPreview', {
            markerIndex,
            routeId,
            position: [markerFeature.markerGeometry.coordinates[0], markerFeature.markerGeometry.coordinates[1]]
          });
        }
      }
      
      map.on("mousemove", onMove);
      map.once("mouseup", onUp);
      document.addEventListener('keydown', onKeyDown);
    }

    function touchStart(e: any) {
      if (e.points.length !== 1) return;
      // Prevent the default map drag behavior.
      e.preventDefault();

      map.on("touchmove", onMove);
      map.once("touchend", onUp);
    }
    
    // Handle ESC key to cancel drag
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        // Cancel drag and preview
        map.off("mousemove", onMove);
        map.off("touchmove", onMove);
        document.removeEventListener('keydown', onKeyDown);
        
        // Reset marker to original position
        const geoJsonSource = map.getSource(id) as mapboxgl.GeoJSONSource;
        if (geoJsonSource && markerFeature) {
          // Find original position from state
          const activeRoute = getters.activeRoute;
          const originalMarker = activeRoute.markers.find(
            (m: MarkerFeature) => m.markerProperties.id === markerFeature.markerProperties.id
          );
          if (originalMarker) {
            markerFeature.markerGeometry.coordinates = [...originalMarker.markerGeometry.coordinates];
            // @ts-ignore
            geoJsonSource.setData(markerFeature.toGeoJson());
          }
        }
        
        // Cancel preview if active
        if (state.dragPreview.isDragging) {
          dispatch('cancelDragPreview');
        }
        
        canvas.style.cursor = "move";
      }
    }

    if (functionRefs[id] != null) {
      functionRefs[id].forEach((ref) => {
        map.off("mouseenter", id, ref);
        map.off("mouseleave", id, ref);
        map.off("mousedown", id, ref);
        map.off("touchstart", id, ref);
      });
    }

    functionRefs[id] = [onMove, onUp, enterStyleMarker, leaveStyleMarker, mouseDown, touchStart];

    map.on("mouseenter", id, enterStyleMarker);
    map.on("mouseleave", id, leaveStyleMarker);
    map.on("mousedown", id, mouseDown);
    map.on("touchstart", id, touchStart);

    let startClickTime: number | null = null;
    let endClickTime: number | null = null;
  },

  async markerMoved({ state, commit, dispatch, getters }: PlanTrailActionContext, payload: MarkerFeature | { marker: MarkerFeature, oldPosition: number[], routeKey?: string | null }) {
    // Support both old call signature (just marker) and new one (marker + oldPosition)
    const marker: MarkerFeature = 'marker' in payload ? payload.marker : payload;
    const oldPosition: number[] | null = 'oldPosition' in payload ? payload.oldPosition : null;

    const map = getMap();
    if (map.getSource('marker')) {
      map.removeLayer('marker');
      map.removeSource('marker');
    }

    // Update marker
    marker.markerProperties.isSnap = state.isSnap;
    await commit('updateMarker', marker);

    // Scope to the marker's own route when provided (whole-trail editing);
    // fall back to the active route for legacy callers
    const routeKey: string | null = ('routeKey' in (payload as any) && (payload as any).routeKey)
      ? (payload as any).routeKey
      : state.activeRouteKey;
    const targetRoute = routeKey ? state.routes[routeKey] : getters.activeRoute;
    if (!targetRoute) return;

    // Find marker index and capture old adjacent linestrings BEFORE they're replaced
    const markers = targetRoute.markers;
    const index = markers.findIndex(m => m.markerProperties.id == marker.markerProperties.id);

    const oldLineStrings: { index: number; lineString: any }[] = [];
    if (oldPosition) {
      // Capture adjacent linestrings before replacement
      if (index > 0 && targetRoute.lineStrings[index - 1]) {
        oldLineStrings.push({ index: index - 1, lineString: JSON.parse(JSON.stringify(targetRoute.lineStrings[index - 1])) });
      }
      if (index < targetRoute.lineStrings.length && targetRoute.lineStrings[index]) {
        oldLineStrings.push({ index: index, lineString: JSON.parse(JSON.stringify(targetRoute.lineStrings[index])) });
      }
    }

    // Update linestrings (creates provisional lines that will be replaced by GraphHopper)
    await dispatch('updateLineStringsFromMarkerMove', { markerIndex: index, routeKey });

    // Stage decorations track marker positions — refresh them so badges,
    // pins, tethers and the start anchor follow the moved endpoint instead
    // of being left behind at the old position
    await dispatch('updateStageConnectorsOnMap');
    await dispatch('updateTrailEndpointMarkersOnMap');
    await dispatch('updateStartAnchorOnMap');

    // Create the MoveMarkerCommand with old position + old linestrings
    if (oldPosition && routeKey) {
      const newLineStrings: { index: number; lineString: any }[] = [];
      if (index > 0 && targetRoute.lineStrings[index - 1]) {
        newLineStrings.push({ index: index - 1, lineString: JSON.parse(JSON.stringify(targetRoute.lineStrings[index - 1])) });
      }
      if (index < targetRoute.lineStrings.length && targetRoute.lineStrings[index]) {
        newLineStrings.push({ index: index, lineString: JSON.parse(JSON.stringify(targetRoute.lineStrings[index])) });
      }

      state.undoManager.push(new MoveMarkerCommand({
        routeKey,
        markerIndex: index,
        oldPosition,
        newPosition: [...marker.markerGeometry.coordinates],
        oldLineStrings,
        newLineStrings,
      }));
    }
  },

  // Route-specific marker actions
  async addRouteMarkerToMap({state, dispatch }: PlanTrailActionContext, data: {
    marker: MarkerFeature,
    routeId: string,
    markerIndex: number,
    isActive: boolean
  }) {
    const { marker, routeId, markerIndex, isActive } = data;
    const markerFeature = plainToClass(MarkerFeature, marker);
    const layerRegistry = getLayerRegistry();
    const route = state.routes[routeId];

    // Create unique ID for this route marker
    const id = `route-${routeId}-marker-${markerIndex}`;
    marker.markerProperties.id = id;

    // Remove existing marker if it exists
    await dispatch('removeRouteMarkerFromMap', { routeId, markerIndex });

    // Get source and add route/marker properties
    const source = markerFeature.toGeoJSONSourceRaw();
    // Add properties to the feature for identification
    if (source.data && source.data.type === 'Feature') {
      source.data.properties = {
        ...source.data.properties,
        routeId: routeId,
        markerIndex: markerIndex,
        markerId: markerFeature.markerProperties.id
      };
    }

    // Create layer
    const layer = await dispatch("createRouteMarkerLayer", {
      id,
      isActive,
      isFirst: markerIndex === 0,
      isLast: route ? markerIndex === route.markers.length - 1 : false
    });

    // Add layer and source together to avoid race condition
    layerRegistry.addLayer(layer, MapLayerCategory.Annotations, {
      z: isActive ? 13 : 11,
      source: { id, spec: source, replace: true }
    });

    if (isActive) {
      // Pass the actual layer ID to the listeners
      await dispatch('addListenersForRouteMarker', {
        marker: markerFeature,
        routeId,
        layerId: id,
        markerIndex
      });
    }
  },

  async removeRouteMarkerFromMap({ }: PlanTrailActionContext, data: { routeId: string, markerIndex: number }) {
    const { routeId, markerIndex } = data;
    const id = `route-${routeId}-marker-${markerIndex}`;
    safeRemoveLayer(id);
  },

  createRouteMarkerLayer({ }: PlanTrailActionContext, data: { id: string, isActive: boolean, isFirst: boolean, isLast: boolean }): mapboxgl.SymbolLayer {
    const { id, isActive, isFirst, isLast } = data;

    // Determine which icon to use
    const getIconImage = () => {
      if (isFirst) return 'hike-control-point-start';
      if (isLast) return 'hike-control-point-end';
      return 'hike-control-point-mid';
    };

    // Determine icon size based on state
    const getIconSize = () => {
      if (isActive) {
        return isFirst || isLast ? 0.25 : 0.25;
      }
      return isFirst || isLast ? 0.25 : 0.25;
    };

    return {
      id: id,
      type: "symbol",
      source: id,
      layout: {
        "icon-image": getIconImage(),
        "icon-size": getIconSize(),
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
        "icon-anchor": "center",
      },
      paint: {
        "icon-opacity": isActive ? 1.0 : 0.6,
        "icon-halo-color": "#ffffff",
        "icon-halo-width": isActive ? 2 : 1,
        "icon-halo-blur": 0,
      },
    };
  },

  async addListenersForRouteMarker({ state, dispatch }: PlanTrailActionContext, data: {
    marker: MarkerFeature,
    routeId: string,
    layerId: string,
    markerIndex: number
  }) {
    // Only add listeners for active route markers
    const activeRoute = state.activeRouteKey ? state.routes[state.activeRouteKey] : null;
    if (activeRoute && activeRoute.id === data.routeId) {
      // Call addListenersForMarker with the layer ID
      await dispatch('addListenersForMarker', {
        marker: data.marker,
        layerId: data.layerId
      });
    }
  }
};
